import React, { PureComponent } from "react";
import { changeLocation } from "api/Location";
import { isNil } from "lodash";
import { pure } from "recompose";
import { withRouter } from "react-router";

import { roleToUrl } from "services/utils";
import DecisionsSummary from "containers/decisions-summary";
import Countdown from "components/countdown";
import Auth from "routes/Auth";

import { view } from "@risingstack/react-easy-state";
import RoundSwitcherList from "./RoundSwitcherList";
import LinksWrapper from "./LinksWrapper";
import EventEmitter from "services/event-emitter";
import {
  isRoleIntroDismissed,
  ROLE_INTRO_DISMISSED_EVENT,
} from "components/onboarding/welcome/Round1Checklist";

import cn from 'classnames';

const InstructorLeftBar = pure(props => {
  const {
    location,
    instructorRounds,
    currentRound,
    roundInUrl,
    rounds,
    isMenuExpanded,
    isPerformanceFeedbackVisible
  } = props;
  const round = isNil(roundInUrl) ? currentRound : roundInUrl;
  const temporaryUser = Auth.getTemporaryUser();
  // Hide the Onboarding entry once the instructor has finished the
  // guided flow. `user.onboard !== 0` mirrors the InstructorOnboarding
  // container's completion check ([containers/onboarding.js]); if
  // still 0 the sidebar entry remains so the instructor can re-enter
  // mid-flow.
  const authUser = Auth.getUser();
  const isInstructorOnboarded = !!(authUser && authUser.onboard !== 0);

  const welcomeInnerLinks = [
    {
      to: '/instructor/onboarding',
      title: 'Onboarding',
      round,
      hide: isInstructorOnboarded,
    },
    {
      to: '/analysis/bcase/executive-summary',
      title: 'The Business Case',
      round,
    },
    {
      to: '/ceo/analysis/market',
      title: 'Customer Analysis',
      round,
    },
    {
      to: '/instructor/goals-and-targets',
      title: 'Goals & Targets',
    },
  ].filter(link => link.hide !== true);

  const activeRound = rounds.find(item => item.number === round);
  const isActiveRoundFinished = activeRound ? activeRound.is_finished : false;
  // Performance Feedback / Final Scores visibility is scoped to the
  // current game's rounds (rounds prop is loaded per game). Hide both
  // when the game has no rounds; additionally hide Performance
  // Feedback until at least one round has finished.
  const hasAnyRound = Array.isArray(rounds) && rounds.length > 0;
  const hasFinishedRound = Array.isArray(rounds) && rounds.some(r => r.is_finished === true);
  const canShowPerformanceFeedback = isPerformanceFeedbackVisible && hasFinishedRound;
  const canShowFinalScores = hasAnyRound;

  return (
    <div className={cn('left__bar', {
      left__bar__collapsed: !isMenuExpanded,
    })}>
      <div className="left__bar__div">
        <RoundSwitcherList
          round={round}
          currentRound={currentRound}
          rounds={instructorRounds}
          location={location}
          isMenuExpanded={isMenuExpanded}
        />
        <Countdown
          currentRound={currentRound}
          rounds={instructorRounds}
          roundInUrl={roundInUrl}
          isMenuExpanded={isMenuExpanded}
        />
        <LinksWrapper
          isMenuExpanded={isMenuExpanded}
          location={location}
          links={[
            {
              to: "/instructor/dashboard",
              hide: !!temporaryUser,
              round,
              title: "Dashboard"
            },
            {
              to: '/instructor/welcome',
              title: 'Welcome',
              hide: !!temporaryUser,
              round,
              innerLinks: welcomeInnerLinks,
            },
            {
              to: '/instructor/game-setup',
              title: 'Game Admin',
              hide: !!temporaryUser,
              round,
              innerLinks: [
                {
                  to: '/instructor/teams-roles',
                  title: 'Teams & Roles',
                },
                {
                  to: '/instructor/rounds',
                  title: 'Rounds',
                  round,
                },
                {
                  to: '/instructor/peer-review',
                  title: 'Team Evaluation',
                },
                {
                  to: '/instructor/grading',
                  title: 'Grading',
                  round,
                },
              ],
            },
            {
              to: '/instructor/monitoring',
              title: 'Monitoring',
              hide: !temporaryUser,
              round,
              innerLinks: [
                {
                  to: "/decisions/ceo",
                  round,
                  title: "CEO Decisions"
                },
                {
                  to: "/decisions/product-development",
                  round,
                  title: "Product Management"
                },
                {
                  to: "/decisions/product-marketing",
                  round,
                  title: "Product Marketing"
                },
                {
                  to: "/decisions/operations",
                  round,
                  title: "Operations Decisions"
                },
                {
                  to: "/decisions/finance",
                  round,
                  title: "Finance Decisions"
                },
                {
                  to: '/ceo/audit-trail',
                  alternatives: [
                    '/pd/audit-trail',
                    '/pm/audit-trail',
                    '/op/audit-trail',
                    '/fn/audit-trail',
                  ],
                  title: 'Audit Trail',
                  round,
                },
              ],
            },
            {
              to: '/instructor/results',
              title: 'Results',
              hide: !!temporaryUser,
              round,
              innerLinks: [
                ...(canShowPerformanceFeedback ? [
                  {
                    to: '/instructor/performance-feedback',
                    title: 'Performance Feedback',
                    round,
                  }
                ] : []),
                ...(canShowFinalScores ? [
                  {
                    to: '/instructor/final-scores',
                    title: 'Final Scores',
                    round,
                  }
                ] : []),
              ],
            },
            {
              to: '/instructor/results',
              title: 'Results',
              hide: (!temporaryUser || !isActiveRoundFinished),
              innerLinks: [
                {
                  to: '/analysis/team-reports',
                  title: 'Team Reports',
                  round,
                },
              ],
            },
          ]}
        />
      </div>
    </div>
  );
});

const LeftBar = pure(({ isStudent, noGameYet, ...props }) => {
  // Even in the noGameYet locked state we render the LeftBar so the
  // student has visual context of the app shell behind the popup.
  // The OnboardingPage's dim backdrop covers the LeftBar area,
  // making its links non-interactive without hiding them.
  return isStudent ? <StudentLeftBar {...props} /> : <InstructorLeftBar {...props} />;
})

class StudentLeftBar extends PureComponent {
  constructor(props) {
    super(props);

    const { pathname } = props.location;

    this.state = {
      showMenu: false,
      showNavigation: false,
      isPeerReviewVisible: true,
      showTeamReportsMenu:
        [
          "/analysis/bcase",
          "/ceo/analysis/market",
          "/pd/analysis/market",
          "/pm/analysis/market",
          "/op/analysis/market",
          "/fn/analysis/market",
          "/team-reports"
        ].includes(pathname) ||
        pathname.indexOf("/analysis/team-reports") === 0,
      showDecisionsMenu: [
        "/decisions/ceo",
        "/decisions/product-development",
        "/decisions/product-marketing",
        "/decisions/operations",
        "/decisions/finance"
      ].includes(pathname),
      // Mirrors the persisted "user clicked Continue on the role-intro
      // screen" flag. Used to hide every non-Welcome nav item while
      // the role-intro screen is on the /onboarding page so we nudge
      // the student to click Continue instead of jumping elsewhere.
      roleIntroDismissed: isRoleIntroDismissed(),
    };
    this.handleRoleIntroDismissed = () => {
      this.setState({ roleIntroDismissed: true });
    };
  }

  componentDidMount() {
    EventEmitter.getInstance().on(
      ROLE_INTRO_DISMISSED_EVENT,
      this.handleRoleIntroDismissed
    );
  }

  componentWillUnmount() {
    EventEmitter.getInstance().off(
      ROLE_INTRO_DISMISSED_EVENT,
      this.handleRoleIntroDismissed
    );
  }

  showDecisionsMenu = e => {
    e.preventDefault();

    this.setState(
      {
        showDecisionsMenu: true
      },
      () => {
        document.addEventListener("click", this.hideDecisionsMenu);
      }
    );
  };

  hideDecisionsMenu = e => {
    if (e.target.toString().indexOf("decisions") > -1) {
      return;
    } else {
      this.setState(
        {
          showDecisionsMenu: false
        },
        () => {
          document.removeEventListener("click", this.hideDecisionsMenu);
        }
      );
    }
  };

  showTeamReportsMenu = event => {
    event.preventDefault();

    this.setState(
      {
        showTeamReportsMenu: true
      },
      () => {
        document.addEventListener("click", this.closeTeamReports);
      }
    );
  };

  closeTeamReports = e => {
    if (
      e.target.toString().indexOf("analysis") > -1 ||
      e.target.toString().indexOf("team-reports") > -1
    ) {
      return;
    } else {
      this.setState({ showTeamReportsMenu: false }, () => {
        document.removeEventListener("click", this.closeTeamReports);
      });
    }
  };

  showMenu = event => {
    event.preventDefault();

    this.setState(
      {
        showMenu: true
      },
      () => {
        document.addEventListener("click", this.closeMenu);
      }
    );
  };

  closeMenu = () => {
    this.setState({ showMenu: false }, () => {
      document.removeEventListener("click", this.closeMenu);
    });
  };

  renderYears() {
    const { years, currentRound } = this.props;

    if (years.data.length > 0) {
      years.data.map(el => {
        return <li>Year : {el <= currentRound ? el : ""}</li>;
      });
    } else {
      return <li>Year : {years.data[0]}</li>;
    }
  }

  showPositionsMenu = event => {
    event.preventDefault();

    const { position } = this.props;

    const route = `/decisions/${position.toString().toLowerCase()}`;
    changeLocation(route);

    this.setState(
      {
        showNavigation: true
      },
      () => {
        document.addEventListener("click", this.closePositionsMenu);
      }
    );
  };

  closePositionsMenu = e => {
    if (e.target.toString().indexOf("decisions") > -1) {
      return;
    } else {
      this.setState({ showNavigation: false }, () => {
        document.removeEventListener("click", this.closePositionsMenu);
      });
    }
  };

  render() {
    const {
      currentRound,
      rounds,
      roundInUrl,
      gameRound,
      position,
      location,
      isMenuExpanded,
      isPerformanceFeedbackVisible
    } = this.props;

    const { isPeerReviewVisible, roleIntroDismissed } = this.state;

    const round = isNil(roundInUrl) ? currentRound : roundInUrl;
    const roleUrl = roleToUrl(position);
    // Mirror the instructor rule: hide Performance Feedback until at
    // least one round for this game has finished. Still respects the
    // parent's isPerformanceFeedbackVisible.
    //
    // Additionally hide the link while the currently VIEWED round is
    // still active — the link routes to /performance-feedback?round=<round>
    // and PF doesn't exist for an unfinished round. Prior-round PF
    // remains accessible via the "Open performance feedback" link in
    // the Welcome checklist, which explicitly targets the prior round.
    const hasFinishedRound = Array.isArray(rounds) && rounds.some(r => r.is_finished === true);
    const viewedRoundFinished = Array.isArray(rounds) && rounds.some(
      r => Number(r.number) === Number(round) && r.is_finished === true
    );
    const canShowPerformanceFeedback =
      isPerformanceFeedbackVisible && hasFinishedRound && viewedRoundFinished;

    // On first onboarding visit the role-intro screen (Round1Checklist's
    // pre-checklist "Your Role: X" card) is on screen. Hide every
    // non-Welcome nav item until the student clicks Continue so the
    // sidebar nudges them toward that action instead of scattering.
    // The gate lifts as soon as the role intro is dismissed (localStorage
    // flag + ROLE_INTRO_DISMISSED_EVENT).
    //
    // Only round 1 has the intro — round 2+ uses RoundChecklist which
    // has no intro screen, so the gate would otherwise hide the whole
    // nav on round 2+ any time the current role's round1 dismissal key
    // isn't set (e.g. after role rotation).
    const onOnboarding = location
      && location.pathname
      && location.pathname.indexOf('/onboarding') === 0;
    const hideNonWelcomeNav = onOnboarding && round === 1 && !roleIntroDismissed;

    return (
      <div className={cn('left__bar', {
        left__bar__collapsed: !isMenuExpanded,
      })}>
        <div className="left__bar__div">
          <RoundSwitcherList
            round={round}
            currentRound={currentRound}
            rounds={rounds}
            location={location}
            isMenuExpanded={isMenuExpanded}
          />
          <Countdown
            currentRound={currentRound}
            rounds={rounds}
            roundInUrl={roundInUrl}
            gameRound={gameRound}
            isMenuExpanded={isMenuExpanded}
          />
          <LinksWrapper
            currentRound={currentRound}
            roundInUrl={roundInUrl}
            position={position}
            location={location}
            isMenuExpanded={isMenuExpanded}
            links={[
              /*{
                to: "/dashboard",
                round,
                title: "Dashboard",
                hide: false
              },*/
              {
                to: '/onboarding',
                round,
                title: 'Welcome',
                hide: false,
              },
              {
                to: `/expand/research`,
                round: round,
                hide: round === 1 || hideNonWelcomeNav,
                title: "Research",
                innerLinks: [
                  {
                    to: `/analysis/bcase/executive-summary`,
                    round: round,
                    title: "Business Case"
                  },
                  {
                    to: `/${roleUrl}/analysis/market`,
                    round: round,
                    title: "Customer Analysis"
                  },
                  {
                    to: "/analysis/team-reports",
                    round: round - 1,
                    title: "Prior Round Team Report",
                  },
                ],
              },
              {
                to: `/expand/research`,
                round: round,
                hide: round !== 1 || hideNonWelcomeNav,
                title: "Research",
                innerLinks: [
                  {
                    to: `/analysis/bcase/executive-summary`,
                    round: round,
                    title: "Business Case"
                  },
                  {
                    to: `/${roleUrl}/analysis/market`,
                    round: round,
                    title: "Customer Analysis",
                  },
                ],
              },
              {
                to: '/goals-and-targets',
                round,
                title: 'Goals & Targets',
                hide: hideNonWelcomeNav,
              },
              {
                to: '/round-trivia',
                round,
                title: 'Trivia',
                hide: true,
              },
              {
                to: "/decisions",
                round: round,
                title: "Decisions",
                hide: hideNonWelcomeNav,
                expandable: true,
                innerLinks: [
                  {
                    to: "/decisions/ceo",
                    round: round,
                    title: "CEO",
                  },
                  {
                    to: "/decisions/product-development",
                    round: round,
                    title: "Product Management",
                  },
                  {
                    to: "/decisions/product-marketing",
                    round: round,
                    title: "Product Marketing",
                  },
                  {
                    to: "/decisions/operations",
                    round: round,
                    title: "Operations",
                  },
                  {
                    to: "/decisions/finance",
                    round: round,
                    title: "Finance",
                  },
                ],
              },
              ...(isPeerReviewVisible ? [
                {
                  to: "/peer-review",
                  title: "Peer Review",
                  round: round,
                  hide: hideNonWelcomeNav,
                }
              ] : []),
              {
                to: "/expand/results",
                round: round,
                title: 'Results',
                hide: hideNonWelcomeNav,
                innerLinks: [
                  ...(canShowPerformanceFeedback ? [
                    {
                      to: "/performance-feedback",
                      round: round,
                      title: "Performance Feedback",
                    },
                  ] : []),
                  {
                    to: "/analysis/team-reports",
                    round: round,
                    title: "Team Report",
                  },
                  {
                    to: "/scoring-breakdown",
                    round: round,
                    title: "Scoring Breakdown",
                  },
                ],
              },
              // {
              //   to: "/ceo/knowledge-base",
              //   round: round,
              //   title: "Knowledge Base",
              //   hide: !isNextStepsVisited,
              // },
            ]}
          />
          {this.state.showNavigation ? (
            <DecisionsSummary location={window.location} position={position} />
          ) : null}
        </div>
      </div>
    );
  }
}

export default withRouter(view(LeftBar));



// WEBPACK FOOTER //
// ./src/components/navigation/leftbar/index.js